"use client"

import { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useChat } from "@/contexts/ChatContext"
import {
  MessageSquare, Send, X, Plus, Paperclip, File,
  Check, ArrowLeft, Settings, Camera, UserMinus, User
} from "lucide-react"
import { toast } from "sonner"
import { capitalizeFirstLetter } from "@/lib/utils"
import { linkify } from "@/lib/linkify"

interface AttachedFile {
  file: File
  preview?: string
  id: string
}

// Helper to format date separators
const formatDividerDate = (dateStr: string) => {
  const d = new Date(dateStr)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (d.toDateString() === today.toDateString()) {
    return "Dzisiaj"
  } else if (d.toDateString() === yesterday.toDateString()) {
    return "Wczoraj"
  } else {
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })
  }
}

export default function ChatInterface() {
  const { data: session } = useAuth()
  const {
    isConnected, activeRoomId, rooms, messages,
    users, sendMessage, sendFile, joinRoom,
    startTyping, stopTyping, createDirectRoom, createGroupRoom,
    markAsRead, fetchRoomDetails, loadChatData, isDataLoaded,
    typingUsers
  } = useChat()

  // --- STANY UI ---
  const [isOpen, setIsOpen] = useState(false)
  const [showRoomsList, setShowRoomsList] = useState(true)
  const [newMessage, setNewMessage] = useState("")

  // Modale i Zarządzanie
  const [showCreateRoom, setShowCreateRoom] = useState(false)
  const [showRoomSettings, setShowRoomSettings] = useState(false)
  const [roomType, setRoomType] = useState<'DIRECT' | 'GROUP'>('DIRECT')
  const [newGroupName, setNewGroupName] = useState("")
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [groupImage, setGroupImage] = useState<File | null>(null)

  // Wiadomości i Pliki
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null)
  const [replyToMessage, setReplyToMessage] = useState<any>(null)

  // Referencje
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const activeRoom = rooms.find(room => room.id === activeRoomId)
  const totalUnreadCount = useMemo(() => rooms.reduce((sum, r) => sum + (r._count?.messages || 0), 0), [rooms])

  // --- 1. LOGIKA "SEEN BY" (Kto przeczytał) ---
  const messageReadByUsers = useMemo(() => {
    const userLastReadMsgId: Record<string, string> = {}

    // Znajdź ID ostatniej wiadomości dla każdego usera
    messages.forEach(msg => {
      if (msg.readBy && Array.isArray(msg.readBy)) {
        msg.readBy.forEach((readEntry: any) => {
          if (readEntry.userId !== session?.user?.id) {
            userLastReadMsgId[readEntry.userId] = msg.id
          }
        })
      }
    })

    const output: Record<string, any[]> = {}

    Object.entries(userLastReadMsgId).forEach(([userId, msgId]) => {
      if (!output[msgId]) output[msgId] = []
      const msg = messages.find(m => m.id === msgId)
      // FIX: Bezpieczne pobieranie usera (obsługa płaskiej struktury i zagnieżdżonej)
      const readEntry = msg?.readBy?.find((r: any) => r.userId === userId)
      const userData = (readEntry as any)?.user || readEntry
      const memberData = activeRoom?.members?.find((m: any) => m.userId === userId)?.user

      if (userData || memberData) {
        output[msgId].push(userData || memberData)
      }
    })
    return output
  }, [messages, session?.user?.id, activeRoom])

  // --- 2. LOGIKA STATUSU (OSTATNIO WIDZIANY) ---
  const getLastSeenInfo = (lastOnline: string | Date | null | undefined, isOnline: boolean) => {
    if (isOnline) return { text: "Dostępny", color: "text-green-600 dark:text-green-400", dotColor: "bg-green-500" };
    if (!lastOnline) return { text: "Niedostępny", color: "text-gray-500", dotColor: "bg-gray-400" };

    const lastActiveDate = new Date(lastOnline);
    const now = new Date();
    const diffMs = now.getTime() - lastActiveDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) {
      return { text: "Przed chwilą", color: "text-green-600 dark:text-green-400", dotColor: "bg-green-500" }
    } else if (diffMins < 60) {
      return { text: `${diffMins} min temu`, color: "text-yellow-600 dark:text-yellow-400", dotColor: "bg-yellow-500" }
    } else if (diffHours < 24) {
      return { text: `${diffHours}h temu`, color: "text-orange-600 dark:text-orange-400", dotColor: "bg-orange-500" }
    } else if (diffDays === 1) {
      return { text: "Wczoraj", color: "text-gray-600 dark:text-zinc-400", dotColor: "bg-gray-400" }
    } else if (diffDays < 7) {
      return { text: `${diffDays}d temu`, color: "text-gray-600 dark:text-zinc-400", dotColor: "bg-gray-400" }
    } else {
      return { text: lastActiveDate.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' }), color: "text-gray-500 dark:text-zinc-500", dotColor: "bg-gray-400" }
    }
  }

  // --- 3. SCROLL HANDLING ---
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior, block: "end" })
  }, [])

  // Skok na dół przy zmianie pokoju (bez animacji)
  useLayoutEffect(() => {
    if (activeRoomId && isOpen) {
      scrollToBottom("auto")
      const timer = setTimeout(() => scrollToBottom("auto"), 50)
      return () => clearTimeout(timer)
    }
  }, [activeRoomId, isOpen, scrollToBottom])

  // Płynny scroll przy nowej wiadomości
  useEffect(() => {
    if (messages.length > 0 && isOpen) {
      scrollToBottom("smooth")
    }
  }, [messages.length, isOpen, scrollToBottom]) // Usunięto 'messages' z zależności, dodano 'messages.length' by nie triggerować przy zmianie readBy

  // --- 4. DATA LOADING & READ ---
  useEffect(() => {
    if (!isDataLoaded) {
      loadChatData()
      const retryTimeout = setTimeout(() => { if (!isDataLoaded && isConnected) loadChatData() }, 3000)
      return () => clearTimeout(retryTimeout)
    }
  }, [isConnected, isDataLoaded, loadChatData])

  useEffect(() => {
    if (activeRoomId && isOpen && messages.length > 0) {
      const unread = messages.filter(msg => msg.senderId !== session?.user?.id && (!msg.readBy || !msg.readBy.some(read => read.userId === session?.user?.id)))
      if (unread.length > 0) unread.forEach(msg => markAsRead(msg.id))
    }
  }, [activeRoomId, isOpen, messages, session?.user?.id, markAsRead])



  // --- 5. HELPERS ---
  const getOtherUser = (room: any) => {
    if (room.type !== 'DIRECT') return null
    return room.members?.find((m: any) => m.userId !== session?.user?.id)?.user
  }

  const getRoomDisplayName = (room: any) => {
    if (room.type === 'DIRECT') {
      const otherUser = getOtherUser(room)
      return otherUser?.username ? capitalizeFirstLetter(otherUser.username) : "Użytkownik"
    }
    return room.name || "Grupa bez nazwy"
  }

  const isRoomAdmin = useMemo(() => {
    if (!activeRoom || !session?.user?.id) return false
    return activeRoom.members?.find(m => m.userId === session.user.id)?.role === 'ADMIN'
  }, [activeRoom, session?.user?.id])

  // --- 6. HANDLERS ---
  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachedFiles.length === 0) return
    if (!isConnected) { toast.error('Brak połączenia'); return }
    if (newMessage.trim()) await sendMessage(newMessage, 'TEXT', replyToMessage?.id)
    for (const f of attachedFiles) { await sendFile(f.file, replyToMessage?.id) }
    setNewMessage(""); setAttachedFiles([]); setReplyToMessage(null); stopTyping()
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) { toast.error('Max 50MB'); continue }
      const reader = new FileReader()
      reader.onload = (ev) => setAttachedFiles(prev => [...prev, { file, id: Math.random().toString(36), preview: ev.target?.result as string }])
      reader.readAsDataURL(file)
    }
  }

  const handleCreateRoom = async () => {
    if (roomType === 'DIRECT') {
      if (selectedUsers.length !== 1) return
      await createDirectRoom(selectedUsers[0])
    } else {
      if (!newGroupName.trim()) { toast.error("Podaj nazwę grupy"); return }
      let imageUrl = undefined
      if (groupImage) {
        const formData = new FormData()
        formData.append('file', groupImage)
        const res = await fetch('/api/chat/upload', { method: 'POST', body: formData })
        if (res.ok) {
          const data = await res.json()
          imageUrl = data.url
        }
      }
      await createGroupRoom(newGroupName, selectedUsers, undefined, imageUrl)
    }
    setShowCreateRoom(false); setSelectedUsers([]); setNewGroupName("")
  }

  const removeMember = async (userId: string) => {
    if (!activeRoomId) return
    const res = await fetch(`/api/chat/rooms/${activeRoomId}/members`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId }) })
    if (res.ok) { await fetchRoomDetails(activeRoomId); toast.success("Usunięto członka") }
  }

  const updateGroupLogo = async (file: File) => {
    const formData = new FormData(); formData.append('file', file)
    const uploadRes = await fetch('/api/chat/upload', { method: 'POST', body: formData })
    if (uploadRes.ok) {
      const data = await uploadRes.json()
      await fetch(`/api/chat/rooms/${activeRoomId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ imageUrl: data.url }) })
      await fetchRoomDetails(activeRoomId!); toast.success("Zmieniono logo")
    }
  }

  if (!isConnected && !isOpen) return null

  // Helper variables for header status
  const otherUserForStatus = activeRoom ? getOtherUser(activeRoom) : null;
  const statusInfo = otherUserForStatus ? getLastSeenInfo(otherUserForStatus.lastOnline, otherUserForStatus.isOnline) : null;

  return (
    <>
      {/* FAB */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
          {totalUnreadCount > 0 && isDataLoaded && (
            <div className="bg-white dark:bg-zinc-800 px-4 py-2 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-700 animate-bounce mb-1 relative">
              <p className="text-[11px] font-black text-theme uppercase tracking-tight text-nowrap">Nowa wiadomość!</p>
              <div className="absolute -bottom-1 right-6 w-3 h-3 bg-white dark:bg-zinc-800 border-r border-b border-zinc-200 dark:border-zinc-700 rotate-45"></div>
            </div>
          )}
          <button onClick={() => setIsOpen(true)} className="transition-all hover:scale-110 active:scale-95 group relative">
            {totalUnreadCount > 0 && (
              <>
                <div className="absolute inset-0 bg-theme rounded-2xl animate-ping opacity-30"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-theme via-blue-400 to-theme rounded-2xl blur opacity-50 group-hover:opacity-80 transition duration-1000 animate-pulse"></div>
              </>
            )}
            <div className={`relative p-4 rounded-2xl shadow-2xl text-white transition-all duration-500 bg-theme ${totalUnreadCount > 0 ? 'scale-110 shadow-theme/40' : 'shadow-black/20'}`}>
              <MessageSquare className={`w-7 h-7 ${totalUnreadCount > 0 ? 'animate-bounce' : ''}`} />
              {totalUnreadCount > 0 && (
                <div className="absolute -top-3 -right-3 bg-red-600 text-white text-[11px] font-black h-7 w-7 rounded-full border-[3px] border-white dark:border-zinc-900 flex items-center justify-center shadow-lg">
                  {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                </div>
              )}
            </div>
          </button>
        </div>
      )}

      {isOpen && (
        <div className={`fixed z-[60] bg-white dark:bg-zinc-950 shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${window.innerWidth < 1024 ? 'inset-0' : 'bottom-6 right-6 w-[950px] h-[700px] rounded-3xl border border-zinc-200 dark:border-zinc-800'}`}>
          <div className="flex flex-1 overflow-hidden">

            {/* SIDEBAR */}
            <aside className={`${showRoomsList ? 'flex' : 'hidden lg:flex'} flex-col w-full lg:w-80 border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50 backdrop-blur-xl`}>
              <div className="p-4 flex items-center justify-between border-b dark:border-zinc-800">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-black dark:text-white">Czat</h2>
                  <button onClick={() => setShowCreateRoom(true)} className="p-1.5 hover:bg-theme/10 text-theme rounded-lg transition-all" title="Nowa rozmowa"><Plus className="w-5 h-5" /></button>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-red-50 text-zinc-400 hover:text-red-500 rounded-xl transition-colors md:hidden"><X className="w-6 h-6" /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {rooms.map(room => {
                  const otherUser = getOtherUser(room)
                  const displayImage = room.type === 'DIRECT' ? otherUser?.avatarUrl : room.imageUrl
                  return (
                    <button key={room.id} onClick={() => { joinRoom(room.id); if (window.innerWidth < 1024) setShowRoomsList(false); }} className={`w-full flex items-center gap-3 p-3 rounded-2xl transition-all group ${activeRoomId === room.id ? 'bg-white dark:bg-zinc-800 shadow-lg ring-1 ring-zinc-200 dark:ring-zinc-700' : 'hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50'}`}>
                      <div className="relative flex-shrink-0">
                        {displayImage ? <img src={displayImage} className="w-12 h-12 rounded-xl object-cover ring-1 ring-black/5" /> : <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold bg-gradient-to-br ${room.type === 'DIRECT' ? 'from-blue-400 to-indigo-600' : 'from-emerald-400 to-teal-600'}`}>{getRoomDisplayName(room).charAt(0)}</div>}
                        {otherUser?.isOnline && <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white dark:border-zinc-800 rounded-full" />}
                      </div>
                      <div className="flex-1 text-left overflow-hidden">
                        <div className="font-bold text-sm dark:text-zinc-100 truncate group-hover:text-theme transition-colors">{getRoomDisplayName(room)}</div>
                        <div className="text-xs text-zinc-500 truncate mt-0.5 font-medium">{room.messages?.[0]?.content || "Zacznij rozmowę..."}</div>
                      </div>
                      {room._count?.messages > 0 && <div className="bg-theme text-white text-[10px] font-bold h-5 min-w-[20px] rounded-full flex items-center justify-center shadow-md">{room._count.messages}</div>}
                    </button>
                  )
                })}
              </div>
            </aside>

            {/* CHAT WINDOW */}
            <main className={`${!showRoomsList ? 'flex' : 'hidden lg:flex'} flex-1 flex-col bg-white dark:bg-zinc-950 relative`}>
              {activeRoomId ? (
                <>
                  <header className="p-4 border-b dark:border-zinc-800 flex items-center justify-between bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md z-10">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <button onClick={() => setShowRoomsList(true)} className="lg:hidden p-2 -ml-2 hover:bg-zinc-100 rounded-full flex-shrink-0"><ArrowLeft className="w-6 h-6" /></button>

                      {/* HEADER INFO & STATUS */}
                      <div>
                        <div className="font-bold text-zinc-900 dark:text-white truncate text-base leading-tight">{getRoomDisplayName(activeRoom)}</div>
                        {activeRoom?.type === 'DIRECT' && statusInfo && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <div className={`w-2 h-2 rounded-full ${statusInfo.dotColor} animate-pulse`}></div>
                            <span className={`text-[10px] font-semibold ${statusInfo.color}`}>{statusInfo.text}</span>
                          </div>
                        )}
                        {activeRoom?.type === 'GROUP' && (
                          <div className="text-[10px] font-medium text-zinc-500">{activeRoom.members.length} członków</div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-1">
                      <button onClick={() => setShowRoomSettings(true)} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl text-zinc-400" title="Ustawienia"><Settings className="w-5 h-5" /></button>
                      <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-red-50 text-zinc-400 hover:text-red-500 rounded-xl transition-colors" title="Zamknij czat"><X className="w-6 h-6" /></button>
                    </div>
                  </header>

                  <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-1 scroll-smooth">
                    {messages.map((msg, index) => {
                      const isMe = msg.senderId === session?.user?.id;
                      const prevMsg = messages[index - 1];
                      
                      // 1. Sprawdź czy nowa data (inny dzień)
                      const msgDate = new Date(msg.createdAt);
                      const prevMsgDate = prevMsg ? new Date(prevMsg.createdAt) : null;
                      const isDifferentDay = !prevMsgDate || 
                        msgDate.getDate() !== prevMsgDate.getDate() ||
                        msgDate.getMonth() !== prevMsgDate.getMonth() ||
                        msgDate.getFullYear() !== prevMsgDate.getFullYear();

                      // 2. Sprawdź czy to nowa sekwencja (zmiana nadawcy, >5 min różnicy lub zmiana dnia)
                      const isSameSender = prevMsg && prevMsg.senderId === msg.senderId;
                      const timeDiffMinutes = prevMsg ? (new Date(msg.createdAt).getTime() - new Date(prevMsg.createdAt).getTime()) / 60000 : Infinity;
                      const isNewSequence = !isSameSender || timeDiffMinutes > 5 || isDifferentDay;

                      // 3. Seen By
                      const seenByUsers = messageReadByUsers[msg.id] || [];



                      return (
                        <div key={msg.id} className="flex flex-col">
                          {/* Separator dni */}
                          {isDifferentDay && (
                            <div className="flex items-center justify-center my-6 animate-in fade-in duration-300">
                              <div className="border-t border-zinc-200 dark:border-zinc-800 flex-1"></div>
                              <span className="mx-4 px-3 py-1 text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest bg-zinc-50 dark:bg-zinc-900 rounded-full border border-zinc-200 dark:border-zinc-800">
                                {formatDividerDate(msg.createdAt)}
                              </span>
                              <div className="border-t border-zinc-200 dark:border-zinc-800 flex-1"></div>
                            </div>
                          )}

                          <div className={`flex flex-col group/bubble ${isNewSequence ? 'mt-4' : 'mt-1'}`}>
                            <div className={`flex items-end gap-2.5 ${isMe ? 'justify-end' : 'justify-start'}`}>

                              {/* AVATAR (Tylko dla innych i tylko przy nowej sekwencji) */}
                              {!isMe && (
                                <div className="w-8 flex-shrink-0">
                                  {isNewSequence ? (
                                    <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-200 dark:bg-zinc-700 ring-1 ring-zinc-100 dark:ring-zinc-800 shadow-sm">
                                      {msg.sender?.avatarUrl ? (
                                        <img src={msg.sender.avatarUrl} className="w-full h-full object-cover" alt="avatar" />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-zinc-500 bg-zinc-200 dark:bg-zinc-700">
                                          {msg.sender?.username?.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="w-8" />
                                  )}
                                </div>
                              )}

                              {/* DYMEK I INNE PRZYCISKI */}
                              <div className={`flex flex-col max-w-[70%] lg:max-w-[65%] ${isMe ? 'items-end' : 'items-start'}`}>
                                {isNewSequence && !isMe && activeRoom?.type === 'GROUP' && (
                                  <span className="text-[11px] font-bold text-zinc-500 dark:text-zinc-400 mb-1 ml-1 select-none">
                                    {capitalizeFirstLetter(msg.sender?.username || "Użytkownik")}
                                  </span>
                                )}

                                {isNewSequence && msg.replyTo && (
                                  <div className={`text-[10px] bg-zinc-100 dark:bg-zinc-800 px-3 py-1 rounded-t-xl border-l-2 border-theme opacity-70 mb-[-4px] truncate text-zinc-600 dark:text-zinc-400 max-w-full ${isMe ? 'mr-0' : 'ml-0'}`}>
                                    <span className="font-bold">{msg.replyTo.sender.username}:</span> {msg.replyTo.content}
                                  </div>
                                )}

                                <div className="flex items-end gap-2">
                                  {isMe && (
                                    <span className="text-[9px] text-zinc-400/60 dark:text-zinc-500/60 select-none font-medium whitespace-nowrap self-end mb-1 opacity-50 lg:opacity-0 lg:group-hover/bubble:opacity-100 transition-opacity duration-150">
                                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}

                                  <div className={`relative px-4 py-2.5 text-sm shadow-sm transition-all whitespace-pre-wrap
                                    ${isMe
                                      ? `bg-theme text-white shadow-theme/10 ${isNewSequence ? 'rounded-2xl rounded-br-none' : 'rounded-2xl'}`
                                      : `bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 ${isNewSequence ? 'rounded-2xl rounded-bl-none' : 'rounded-2xl'}`
                                    }
                                  `}>
                                    {msg.type === 'TEXT' && linkify(msg.content)}
                                    {msg.type === 'IMAGE' && (
                                      <img
                                        src={msg.fileUrl}
                                        className="rounded-lg max-h-64 cursor-pointer hover:opacity-90 bg-zinc-200"
                                        onClick={() => setFullscreenImage(msg.fileUrl || null)}
                                        alt=""
                                        onLoad={() => scrollToBottom("smooth")}
                                      />
                                    )}
                                    {msg.type === 'FILE' && (() => {
                                      const isVideo = msg.fileType?.startsWith('video/') ||
                                                      msg.fileName?.toLowerCase().endsWith('.mp4') ||
                                                      msg.fileName?.toLowerCase().endsWith('.webm') ||
                                                      msg.fileName?.toLowerCase().endsWith('.mov') ||
                                                      msg.fileName?.toLowerCase().endsWith('.ogg');

                                      if (isVideo) {
                                        return (
                                          <div className="space-y-2 max-w-sm">
                                            <video
                                              src={msg.fileUrl}
                                              controls
                                              className="rounded-lg max-h-64 bg-black/10 max-w-full"
                                              preload="metadata"
                                            />
                                            <a href={msg.fileUrl} target="_blank" className="flex items-center gap-3 p-2 bg-black/5 dark:bg-white/5 rounded-lg">
                                              <File className="w-4 h-4 text-theme" />
                                              <span className="truncate max-w-[150px] font-medium text-xs dark:text-white">{msg.fileName}</span>
                                            </a>
                                          </div>
                                        )
                                      }

                                      return (
                                        <a href={msg.fileUrl} target="_blank" className="flex items-center gap-3 p-2 bg-black/5 dark:bg-white/5 rounded-lg">
                                          <File className="w-4 h-4 text-theme" />
                                          <span className="truncate max-w-[120px] font-medium text-xs dark:text-white">{msg.fileName}</span>
                                        </a>
                                      )
                                    })()}

                                    <button onClick={() => setReplyToMessage(msg)} className={`absolute top-1/2 -translate-y-1/2 p-2 bg-white dark:bg-zinc-700 shadow-md rounded-full opacity-0 group-hover/bubble:opacity-100 transition-opacity z-20 ${isMe ? '-left-10' : '-right-10'}`}><Plus className="w-4 h-4 rotate-45 text-zinc-400" /></button>
                                  </div>

                                  {!isMe && (
                                    <span className="text-[9px] text-zinc-400/60 dark:text-zinc-500/60 select-none font-medium whitespace-nowrap self-end mb-1 opacity-50 lg:opacity-0 lg:group-hover/bubble:opacity-100 transition-opacity duration-150">
                                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* SEEN BY (Pod dymkiem) */}
                            {seenByUsers.length > 0 && (
                              <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end mr-1' : 'justify-start ml-12'} animate-in slide-in-from-top-1 duration-300`}>
                                <div className="flex -space-x-1.5 overflow-hidden">
                                  {seenByUsers.map((user: any) => (
                                    <div key={user.id} className="relative inline-block border border-white dark:border-zinc-950 rounded-full" title={`Wyświetlono przez: ${user.username}`}>
                                      {user.avatarUrl ? <img src={user.avatarUrl} alt={user.username} className="w-3.5 h-3.5 rounded-full object-cover" /> : <div className="w-3.5 h-3.5 rounded-full bg-zinc-300 flex items-center justify-center text-[8px] font-bold text-zinc-600">{user.username?.charAt(0)}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}

                    {/* Wskaźnik pisania (Typing Indicator) */}
                    {typingUsers && typingUsers.size > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 animate-in fade-in duration-200">
                        <div className="flex gap-1 items-center bg-zinc-100 dark:bg-zinc-800 px-3 py-1.5 rounded-2xl shadow-sm">
                          <div className="flex gap-0.5 items-center mr-1">
                            <span className="w-1.5 h-1.5 bg-zinc-400 dark:bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 bg-zinc-400 dark:bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 bg-zinc-400 dark:bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          <span className="italic font-medium text-[11px] text-zinc-600 dark:text-zinc-400">
                            {Array.from(typingUsers.values()).map(u => capitalizeFirstLetter(u.username)).join(', ')} 
                            {typingUsers.size === 1 ? ' pisze...' : ' piszą...'}
                          </span>
                        </div>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>

                  {/* FOOTER */}
                  <footer className="p-4 lg:p-6 border-t dark:border-zinc-800 bg-white dark:bg-zinc-950">
                    <div className="max-w-4xl mx-auto">
                      {/* Reply Card */}
                      {replyToMessage && (
                        <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-900/40 p-3 rounded-t-2xl border-t border-x border-zinc-200 dark:border-zinc-800 animate-in slide-in-from-bottom-2">
                          <div className="flex items-center gap-2 truncate text-xs">
                            <div className="w-1 h-8 bg-theme rounded-full" />
                            <div className="truncate">
                              <p className="font-bold text-theme uppercase text-[10px]">Odpowiedź dla {replyToMessage.sender.username}</p>
                              <p className="text-zinc-500 truncate">{replyToMessage.content}</p>
                            </div>
                          </div>
                          <button onClick={() => setReplyToMessage(null)} className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-full text-zinc-400 hover:text-zinc-600"><X className="w-4 h-4" /></button>
                        </div>
                      )}

                      {/* Attached Files Preview Card */}
                      {attachedFiles.length > 0 && (
                        <div className={`flex flex-wrap gap-2 p-3 bg-zinc-50 dark:bg-zinc-900/40 border-x border-zinc-200 dark:border-zinc-800 border-t border-t-zinc-100 dark:border-t-zinc-800/50 ${!replyToMessage ? 'rounded-t-2xl border-t' : ''} animate-in slide-in-from-bottom-2`}>
                          {attachedFiles.map(file => (
                            <div key={file.id} className="relative group bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-2 rounded-xl flex items-center gap-2 max-w-[200px] shadow-sm">
                              {file.preview && file.file.type.startsWith('image/') ? (
                                <img src={file.preview} alt="preview" className="w-8 h-8 rounded object-cover" />
                              ) : file.preview && file.file.type.startsWith('video/') ? (
                                <video src={file.preview} className="w-8 h-8 rounded object-cover bg-black/10" muted />
                              ) : (
                                <div className="w-8 h-8 rounded bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0">
                                  <File className="w-4 h-4 text-zinc-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0 pr-4">
                                <p className="text-[10px] font-bold truncate text-zinc-700 dark:text-zinc-300">{file.file.name}</p>
                                <p className="text-[8px] text-zinc-400">{(file.file.size / 1024 / 1024).toFixed(2)} MB</p>
                              </div>
                              <button
                                onClick={() => setAttachedFiles(prev => prev.filter(f => f.id !== file.id))}
                                className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow-md lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Main Message Input Bar */}
                      <div className={`flex items-end gap-2 bg-zinc-100 dark:bg-zinc-900 p-2 border border-zinc-200 dark:border-zinc-800 transition-all focus-within:ring-2 focus-within:ring-theme/30 focus-within:bg-white dark:focus-within:bg-zinc-800
                        ${replyToMessage || attachedFiles.length > 0 
                          ? 'rounded-b-2xl rounded-t-none border-t-0' 
                          : 'rounded-2xl'
                        }
                      `}>
                        <button onClick={() => fileInputRef.current?.click()} className="p-3 text-zinc-400 hover:text-theme transition-colors"><Paperclip className="w-6 h-6" /></button>
                        <input type="file" ref={fileInputRef} className="hidden" multiple onChange={handleFileUpload} />
                        <textarea rows={1} value={newMessage} onChange={(e) => { setNewMessage(e.target.value); startTyping(); }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }} placeholder="Napisz wiadomość..." className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-3 resize-none dark:text-zinc-100 max-h-32" />
                        <button onClick={handleSendMessage} disabled={!newMessage.trim() && attachedFiles.length === 0} className="p-3 bg-theme text-white rounded-xl shadow-lg transition-all hover:scale-105 active:scale-95"><Send className="w-6 h-6" /></button>
                      </div>
                    </div>
                  </footer>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center relative bg-white dark:bg-zinc-950">
                  <button onClick={() => setIsOpen(false)} className="absolute top-4 right-4 p-2 hover:bg-red-50 text-zinc-400 hover:text-red-500 rounded-xl transition-colors"><X className="w-7 h-7" /></button>
                  <div className="opacity-20 text-zinc-900 dark:text-zinc-100 flex flex-col items-center"><MessageSquare className="w-20 h-20 mb-4" /><p className="font-black uppercase tracking-widest text-sm">Wybierz rozmowę</p></div>
                </div>
              )}
            </main>
          </div>
        </div>
      )}

      {/* MODALS */}
      {showCreateRoom && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-[32px] overflow-hidden shadow-2xl p-8 space-y-6">
            <div className="flex justify-between items-center mb-4 text-zinc-900 dark:text-zinc-100"><h3 className="text-2xl font-black">Nowy czat</h3><button onClick={() => setShowCreateRoom(false)}><X /></button></div>
            <div className="flex bg-zinc-100 dark:bg-zinc-800 p-1.5 rounded-2xl">
              <button onClick={() => setRoomType('DIRECT')} className={`flex-1 py-3 rounded-xl text-sm font-black transition-all ${roomType === 'DIRECT' ? 'bg-white dark:bg-zinc-700 shadow-xl dark:text-zinc-100' : 'text-zinc-400'}`}>Prywatna</button>
              <button onClick={() => setRoomType('GROUP')} className={`flex-1 py-3 rounded-xl text-sm font-black transition-all ${roomType === 'GROUP' ? 'bg-white dark:bg-zinc-700 shadow-xl dark:text-zinc-100' : 'text-zinc-400'}`}>Grupa</button>
            </div>
            {roomType === 'GROUP' && <input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Nazwa grupy..." className="w-full bg-zinc-100 dark:bg-zinc-800 border-none rounded-2xl px-5 py-4 dark:text-zinc-100 focus:ring-2 focus:ring-theme" />}
            <div className="max-h-56 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
              {users.filter(u => u.id !== session?.user?.id).map(user => (
                <div key={user.id} onClick={() => { if (roomType === 'DIRECT') setSelectedUsers([user.id]); else setSelectedUsers(p => p.includes(user.id) ? p.filter(id => id !== user.id) : [...p, user.id]); }} className={`flex items-center gap-4 p-4 rounded-[20px] cursor-pointer transition-all ${selectedUsers.includes(user.id) ? 'bg-theme text-white shadow-theme/20' : 'bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700'}`}>
                  <div className="w-10 h-10 rounded-xl bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-xs font-black text-zinc-500">{user.username.charAt(0)}</div>
                  <span className="flex-1 font-bold text-sm dark:text-zinc-100">{capitalizeFirstLetter(user.username)}</span>
                  {selectedUsers.includes(user.id) && <Check className="w-4 h-4" />}
                </div>
              ))}
            </div>
            <button onClick={handleCreateRoom} disabled={selectedUsers.length === 0 || (roomType === 'GROUP' && !newGroupName.trim())} className="w-full bg-theme text-white py-5 rounded-[24px] font-black shadow-2xl shadow-theme/40 disabled:opacity-30">Utwórz konwersację</button>
          </div>
        </div>
      )}

      {showRoomSettings && activeRoom && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-md animate-in zoom-in" onClick={() => setShowRoomSettings(false)}>
          <div className="bg-white dark:bg-zinc-900 w-full max-w-md rounded-[40px] overflow-hidden p-8 space-y-8 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center text-zinc-900 dark:text-zinc-100"><h3 className="text-xl font-black">Informacje</h3><button onClick={() => setShowRoomSettings(false)} className="p-2 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-full text-zinc-400"><X /></button></div>
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-32 h-32 rounded-[32px] bg-gradient-to-br from-theme to-blue-600 flex items-center justify-center text-4xl text-white font-black overflow-hidden shadow-2xl relative group">
                {activeRoom.imageUrl ? <img src={activeRoom.imageUrl} className="w-full h-full object-cover" /> : activeRoom.name?.charAt(0) || "G"}
                {isRoomAdmin && activeRoom.type !== 'DIRECT' && (
                  <label className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-all">
                    <Camera className="text-white mb-1" /><span className="text-[10px] text-white font-bold uppercase">Zmień</span>
                    <input type="file" className="hidden" onChange={e => e.target.files?.[0] && updateGroupLogo(e.target.files[0])} />
                  </label>
                )}
              </div>
              <h4 className="font-black text-2xl dark:text-zinc-100">{getRoomDisplayName(activeRoom)}</h4>
              <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">{activeRoom.type}</span>
            </div>
            <div className="space-y-4">
              <p className="text-xs font-black text-zinc-400 uppercase tracking-widest border-b dark:border-zinc-800 pb-2">Członkowie ({activeRoom.members.length})</p>
              <div className="max-h-64 overflow-y-auto space-y-3 custom-scrollbar">
                {activeRoom.members.map(member => (
                  <div key={member.id} className="flex items-center gap-4 group/member">
                    <div className="w-12 h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-sm font-black overflow-hidden flex-shrink-0 text-zinc-500">{member.user.avatarUrl ? <img src={member.user.avatarUrl} className="w-full h-full object-cover" /> : member.user.username.charAt(0)}</div>
                    <div className="flex-1 overflow-hidden"><p className="text-sm font-black truncate text-zinc-900 dark:text-zinc-100">{member.user.username}</p><p className="text-[10px] text-zinc-500 uppercase font-bold">{member.role}</p></div>
                    {isRoomAdmin && member.userId !== session?.user?.id && <button onClick={() => removeMember(member.userId)} className="p-2 text-zinc-400 hover:text-red-500 opacity-0 group-hover/member:opacity-100 transition-all"><UserMinus className="w-5 h-5" /></button>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {fullscreenImage && (
        <div className="fixed inset-0 bg-black/98 z-[200] flex items-center justify-center p-4 animate-in fade-in" onClick={() => setFullscreenImage(null)}>
          <button className="absolute top-8 right-8 text-white/50 hover:text-white transition-colors bg-white/10 p-4 rounded-full backdrop-blur-md"><X className="w-8 h-8" /></button>
          <img src={fullscreenImage} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl ring-1 ring-white/10" alt="Fullscreen" />
        </div>
      )}
    </>
  )
}